import { Mail, ShieldCheck, Lock } from "lucide-react";

const SUPPORT_EMAIL = "support@scudononni.it";

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <div className="text-center mb-12 animate-fade-up">
        <Lock size={56} className="mx-auto text-[#1E3A8A] mb-5" />
        <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1E3A8A] leading-tight">
          Informativa sulla Privacy
        </h1>
        <p className="mt-4 text-lg text-gray-600">
          Ai sensi del Regolamento UE 2016/679 (GDPR) e del D.lgs. 196/2003 come modificato dal D.lgs. 101/2018
        </p>
        <p className="mt-2 text-base text-gray-500">
          Ultimo aggiornamento: 27 febbraio 2026
        </p>
      </div>

      <div className="bg-white rounded-[2rem] shadow-lg border-4 border-blue-50 p-6 md:p-12 space-y-8 text-lg md:text-xl text-gray-700 leading-relaxed">
        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3">1. Chi siamo</h2>
          <p>
            <strong>ScudoNonni</strong> ("noi", "il Sito") è un servizio gratuito che aiuta le persone
            anziane a riconoscere truffe online (email, SMS, WhatsApp, telefono).
            Il titolare del trattamento dei dati è raggiungibile all'indirizzo email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#1E3A8A] font-bold underline">
              {SUPPORT_EMAIL}
            </a>.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3">2. Dati che raccogliamo</h2>
          <p className="mb-3">
            ScudoNonni è progettato per <strong>raccogliere il minor numero possibile di dati</strong> (principio di minimizzazione, art. 5.1.c GDPR). In particolare:
          </p>
          <ul className="list-disc pl-8 space-y-2">
            <li>
              <strong>Identificativo anonimo</strong>: un codice casuale generato dal tuo dispositivo
              (memorizzato in <em>localStorage</em>) per contare il numero di verifiche giornaliere.
              Non contiene nome, email o altri dati personali.
            </li>
            <li>
              <strong>Indirizzo IP — in forma anonimizzata</strong>: applichiamo un hash crittografico
              SHA-256 con salt al tuo IP prima di memorizzarlo, così da non poter risalire all'IP originale.
              Lo usiamo solo per il conteggio del limite giornaliero e per prevenire abusi.
            </li>
            <li>
              <strong>Testo o immagini che invii per l'analisi</strong>: vengono inviati al servizio
              di intelligenza artificiale (Google Gemini) solo per il tempo necessario all'analisi e
              <strong> NON vengono memorizzati</strong> sui nostri server. Salviamo unicamente metadati anonimi
              (data, livello di rischio restituito, tipo di input, lunghezza) per statistiche aggregate.
            </li>
            <li>
              <strong>Dati tecnici di navigazione</strong>: indirizzo IP grezzo e User-Agent registrati
              temporaneamente nei log del web server (Cloudflare/Emergent) per sicurezza, conservati al massimo 30 giorni.
            </li>
            <li>
              <strong>Cookie statistici (Google Analytics 4)</strong>: solo previo tuo consenso esplicito.
              Vedi la <a href="/cookie-policy" className="text-[#1E3A8A] underline">Cookie Policy</a> per i dettagli.
            </li>
          </ul>
          <p className="mt-3">
            <strong>Non raccogliamo</strong> nome, cognome, email, numero di telefono, dati bancari o di pagamento.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3">3. Perché trattiamo i dati</h2>
          <ul className="list-disc pl-8 space-y-2">
            <li><strong>Fornire il servizio</strong>: analizzare il messaggio che ci invii.</li>
            <li><strong>Limitare gli abusi</strong>: massimo 3 verifiche al giorno per dispositivo e per indirizzo IP.</li>
            <li><strong>Migliorare il servizio</strong>: analizzare in forma aggregata e anonima le tipologie di truffe segnalate.</li>
            <li><strong>Sicurezza</strong>: prevenire utilizzi malevoli o attacchi al sito.</li>
          </ul>
          <p className="mt-3">
            La base giuridica del trattamento è il <strong>legittimo interesse</strong> (art. 6.1.f GDPR) a
            fornire un servizio di pubblica utilità e a proteggere la sicurezza del sistema.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3">4. Con chi condividiamo i dati</h2>
          <p className="mb-3">
            I testi e le immagini che invii vengono trasmessi a:
          </p>
          <ul className="list-disc pl-8 space-y-2">
            <li>
              <strong>Google LLC (Gemini AI)</strong> — fornitore del servizio di intelligenza artificiale
              che analizza i messaggi. Google tratta i dati secondo le proprie informative, disponibili su{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] underline">
                policies.google.com/privacy
              </a>.
            </li>
            <li>
              <strong>Google LLC (Google Analytics 4)</strong> — <strong>solo se hai dato il consenso</strong>{" "}
              ai cookie statistici, raccogliamo dati di navigazione aggregati e anonimi (pagine visitate,
              durata visita, dispositivo) tramite Google Analytics 4 con anonimizzazione dell'IP attivata.
              Non viene utilizzato per pubblicità. Puoi revocare il consenso in qualsiasi momento dal
              banner cookie (link "Modifica preferenze cookie" nel footer).
            </li>
            <li>
              <strong>Fornitori tecnici</strong> per l'hosting del sito e del database, vincolati da contratto
              di trattamento dati ai sensi dell'art. 28 GDPR.
            </li>
          </ul>
          <p className="mt-3">
            <strong>Non vendiamo né cediamo i tuoi dati a terzi</strong> per finalità di marketing o profilazione.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3">5. Quanto conserviamo i dati</h2>
          <ul className="list-disc pl-8 space-y-2">
            <li><strong>Contatore quotidiano (per dispositivo e IP anonimizzato)</strong>: 24 ore.</li>
            <li><strong>Metadati delle analisi</strong> (data, livello di rischio, tipo input): fino a 90 giorni in forma totalmente anonima per statistiche aggregate. Il testo originale del messaggio o l'immagine analizzata <strong>non vengono conservati</strong>.</li>
            <li><strong>Log tecnici del web server</strong>: fino a 30 giorni.</li>
            <li><strong>Cookie GA4 (se accettati)</strong>: 2 anni — puoi revocare il consenso quando vuoi dal banner cookie.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3">6. Intelligenza Artificiale</h2>
          <p>
            ScudoNonni utilizza un sistema di intelligenza artificiale (Google Gemini 3) per analizzare
            i messaggi. Ai sensi del Regolamento UE 2024/1689 (<strong>EU AI Act</strong>) applicabile
            dal 2 agosto 2026, e in particolare dell'art. 50 sugli obblighi di trasparenza,
            ti informiamo che <strong>il risultato che ricevi è generato da un'IA</strong>. Per i dettagli
            (modello usato, limiti, diritti, misure di sicurezza) consulta la pagina dedicata{" "}
            <a href="/trasparenza-ai" className="text-[#1E3A8A] font-bold underline">Trasparenza AI</a>.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3">7. I tuoi diritti</h2>
          <p className="mb-3">In qualsiasi momento puoi esercitare i diritti previsti dagli artt. 15-22 del GDPR:</p>
          <ul className="list-disc pl-8 space-y-2">
            <li>Accedere ai dati che ti riguardano</li>
            <li>Chiederne la rettifica o la cancellazione</li>
            <li>Opporti al trattamento</li>
            <li>Chiedere la portabilità dei dati</li>
            <li>Revocare il consenso (per i trattamenti che si basano sul consenso)</li>
            <li>Proporre reclamo al <strong>Garante per la protezione dei dati personali</strong> (www.garanteprivacy.it)</li>
          </ul>
          <p className="mt-3">
            Per esercitare questi diritti scrivici a{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#1E3A8A] font-bold underline">
              {SUPPORT_EMAIL}
            </a>.
            Ti risponderemo entro 30 giorni.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3">8. Sicurezza</h2>
          <p>
            Adottiamo misure tecniche e organizzative adeguate per proteggere i dati da accessi non autorizzati,
            perdita o divulgazione. La comunicazione tra il tuo dispositivo e i nostri server avviene tramite
            protocollo HTTPS cifrato.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3">9. Minori</h2>
          <p>
            Il servizio è rivolto a persone adulte. Non raccogliamo intenzionalmente dati di minori di 14 anni.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3">10. Modifiche</h2>
          <p>
            Potremmo aggiornare questa informativa per riflettere modifiche al servizio o alla normativa.
            La versione aggiornata sarà sempre disponibile su questa pagina con la data di ultimo aggiornamento.
          </p>
        </section>

        <section className="bg-[#DBEAFE] rounded-2xl p-6">
          <h2 className="font-heading text-2xl font-bold text-[#1E3A8A] mb-3 flex items-center gap-3">
            <Mail size={28} /> Contattaci
          </h2>
          <p>
            Per qualsiasi domanda sulla privacy:{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="privacy-contact-email" className="text-[#1E3A8A] font-bold underline">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </section>
      </div>
    </div>
  );
}
